// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import { EducBox, ResultCard, InputRow, fmt } from './shared';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Deployment 23 — Sezione G: Calcolatore Buoni Fruttiferi Postali
// Tipologie 2026 e tassi (lordi) come da prompt H1.
const TIPOLOGIE = {
  ordinario: {
    id: 'ordinario',
    label: 'Ordinario',
    duration: 20,
    note: 'Progressivo (rimborso anticipato sempre possibile)',
    progressivo: true,
  },
  '3x2': {
    id: '3x2',
    label: '3x2',
    duration: 6,
    tassoAnnuo: 0.0150,
    note: '1,50% a scadenza · rimborso anticipato dopo 3 anni',
  },
  '4anni': {
    id: '4anni',
    label: '4 anni Risparmio Semplice',
    duration: 4,
    tassoAnnuo: 0.0125,
    note: '1,25% annuo · rimborso anticipato dopo 1 anno',
  },
  minori: {
    id: 'minori',
    label: 'Per Minori',
    duration: 18,
    tassoAnnuo: 0.0460,
    note: '4,60% a scadenza · solo per under 18',
  },
  indicizzato: {
    id: 'indicizzato',
    label: 'Indicizzato inflazione',
    duration: 10,
    tassoAnnuo: null, // calcolato come inflazione + spread
    spread: 0.0050,
    note: 'Inflazione + 0,50% di spread · rendimento variabile',
  },
};

// Tabella tassi BFP Ordinario progressiva
const calcolaRendimentoOrdinario = (capitale, anni) => {
  let totale = capitale;
  for (let anno = 1; anno <= anni; anno++) {
    let tasso;
    if (anno <= 2) tasso = 0.0025;
    else if (anno <= 4) tasso = 0.0050;
    else if (anno <= 6) tasso = 0.0075;
    else if (anno <= 10) tasso = 0.0125;
    else if (anno <= 15) tasso = 0.0175;
    else tasso = 0.0200;
    totale += totale * tasso;
  }
  return totale;
};

const BuoniFruttiferiPostaliCalc = ({ onStepComplete }) => {
  const [importo, setImporto] = useState('10000');
  const [tipologia, setTipologia] = useState('ordinario');
  const [anni, setAnni] = useState('10');
  const [parametri, setParametri] = useState({
    tasso_btp_10anni: 3.20,
    tasso_conto_deposito: 2.80,
    inflazione_default: 2.00,
    data_aggiornamento: '2026-05-05',
  });

  // Carica parametri aggiornabili dall'admin
  useEffect(() => {
    fetch(`${API_URL}/api/calcolatori/buoni-fruttiferi-postali/parametri`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setParametri(prev => ({ ...prev, ...d })); })
      .catch(() => {});
  }, []);

  const tipo = TIPOLOGIE[tipologia];
  const importoNum = parseFloat(importo) || 0;
  const anniNum = parseInt(anni, 10) || 0;

  // Validazione
  const errors = [];
  if (importoNum < 50) errors.push('Importo minimo: €50');
  if (importoNum > 1000000) errors.push('Importo massimo: €1.000.000');
  if (anniNum < 1) errors.push('Durata minima: 1 anno');
  if (anniNum > tipo.duration) errors.push(`Il BFP ${tipo.label} ha durata massima ${tipo.duration} anni`);
  const isValid = errors.length === 0;

  const result = useMemo(() => {
    if (!isValid) return null;

    const inflazione = parametri.inflazione_default / 100;

    // Calcolo capitale finale lordo per tipologia
    let capitaleFinaleLordo;
    if (tipologia === 'ordinario') {
      capitaleFinaleLordo = calcolaRendimentoOrdinario(importoNum, anniNum);
    } else if (tipologia === 'indicizzato') {
      const tassoAnnuo = inflazione + tipo.spread;
      capitaleFinaleLordo = importoNum * Math.pow(1 + tassoAnnuo, anniNum);
    } else {
      // Tasso fisso semplice (annuo composto)
      capitaleFinaleLordo = importoNum * Math.pow(1 + tipo.tassoAnnuo, anniNum);
    }

    const interessiLordi = capitaleFinaleLordo - importoNum;
    const tasse = interessiLordi * 0.125; // 12,5% tassazione agevolata
    const interessiNetti = interessiLordi - tasse;
    const impostaBollo = importoNum <= 5000 ? 0 : 34.20 * anniNum;
    const capitaleFinaleNetto = importoNum + interessiNetti - impostaBollo;
    const rendimentoNettoAnnuo = (interessiNetti / importoNum / anniNum * 100);
    const rendimentoRealeAnnuo = ((1 + interessiNetti / importoNum / anniNum) / (1 + inflazione) - 1) * 100;

    // Confronto con alternative
    const btpLordo = importoNum * (parametri.tasso_btp_10anni / 100) * anniNum;
    const btpNetto = importoNum + btpLordo * (1 - 0.125);

    const contoLordo = importoNum * (parametri.tasso_conto_deposito / 100) * anniNum;
    const contoNetto = importoNum + contoLordo * (1 - 0.26);

    const liquiditaReale = importoNum * Math.pow(1 - inflazione, anniNum);

    return {
      capitaleFinaleNetto,
      interessiNetti,
      rendimentoNettoAnnuo,
      rendimentoRealeAnnuo,
      impostaBollo,
      btpNetto,
      contoNetto,
      liquiditaReale,
    };
  }, [isValid, importoNum, anniNum, tipologia, tipo, parametri]);

  useEffect(() => {
    if (result && onStepComplete) onStepComplete();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!result]);

  // Quando cambia tipologia, clamp anni alla durata massima
  useEffect(() => {
    const max = TIPOLOGIE[tipologia].duration;
    if (parseInt(anni, 10) > max) setAnni(String(max));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipologia]);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="bfp-calc">
      <EducBox text="I Buoni Fruttiferi Postali (BFP) sono titoli di Stato emessi da Cassa Depositi e Prestiti, garantiti dallo Stato italiano e collocati da Poste Italiane. Tassazione agevolata al 12,5%, rimborso del capitale sempre garantito. Esenti da imposta di bollo fino a €5.000." />

      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <InputRow
          label="Importo investito (€)"
          value={importo}
          onChange={setImporto}
          prefix="€"
          min={50}
          max={1000000}
          step={500}
        />
        <div>
          <label className="text-[#888780] text-sm">Tipologia BFP</label>
          <select
            value={tipologia}
            onChange={e => setTipologia(e.target.value)}
            className="w-full mt-1 bg-[#1C1C1A] border border-white/[0.08] text-[#E8E8E4] rounded-md px-3 py-2 text-sm"
            data-testid="bfp-tipologia"
          >
            {Object.values(TIPOLOGIE).map(t => (
              <option key={t.id} value={t.id}>{t.label} (max {t.duration} anni)</option>
            ))}
          </select>
        </div>
        <InputRow
          label={`Anni di detenzione (max ${tipo.duration})`}
          value={anni}
          onChange={setAnni}
          suffix="anni"
          min={1}
          max={tipo.duration}
          step={1}
        />
      </div>

      <p className="text-xs text-[#888780] italic mb-6">{tipo.note}</p>

      {!isValid && (
        <div
          className="mb-6 p-4 rounded-xl text-sm"
          style={{ background: 'rgba(232,112,64,0.1)', border: '1px solid rgba(232,112,64,0.3)', color: '#E87040' }}
          data-testid="bfp-error"
        >
          {errors.map((err, i) => <p key={i}>⚠ {err}</p>)}
        </div>
      )}

      {result && (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <ResultCard label="Capitale finale netto" value={`€${fmt(result.capitaleFinaleNetto)}`} green />
            <ResultCard label="Interessi netti totali" value={`€${fmt(result.interessiNetti)}`} />
            <ResultCard label="Rendimento netto annuo" value={`${result.rendimentoNettoAnnuo.toFixed(2)}%`} gold />
            <ResultCard
              label="Rendimento reale annuo"
              value={`${result.rendimentoRealeAnnuo.toFixed(2)}%`}
              sub={`netto inflazione ${parametri.inflazione_default}%`}
            />
          </div>

          {result.impostaBollo > 0 && (
            <p className="text-xs text-[#888780] mb-4">
              Imposta di bollo applicata: €{fmt(result.impostaBollo)} ({anniNum} × €34,20 — esenzione fino a €5.000).
            </p>
          )}

          {/* Grafico confronto */}
          <div className="mt-6 mb-6">
            <p className="text-sm text-[#888780] mb-3">Confronto a {anniNum} anni — capitale finale netto / reale</p>
            <div style={{ height: 280 }}>
              <Bar
                data={{
                  labels: [
                    `BFP ${tipo.label}`,
                    `BTP 10y (${parametri.tasso_btp_10anni}%)`,
                    `Conto deposito (${parametri.tasso_conto_deposito}%)`,
                    'Liquidità (valore reale)',
                  ],
                  datasets: [{
                    label: 'Capitale finale (€)',
                    data: [result.capitaleFinaleNetto, result.btpNetto, result.contoNetto, result.liquiditaReale],
                    backgroundColor: ['#3D7A52', '#5B8DB8', '#C9A84C', '#888780'],
                    borderRadius: 6,
                  }],
                }}
                options={{
                  indexAxis: 'y',
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { display: false },
                    tooltip: {
                      callbacks: { label: (ctx) => `€${fmt(ctx.parsed.x)}` },
                    },
                  },
                  scales: {
                    x: { ticks: { color: '#888780', callback: (v) => `€${fmt(v)}` }, grid: { color: 'rgba(255,255,255,0.06)' } },
                    y: { ticks: { color: '#E8E8E4', font: { size: 12 } }, grid: { display: false } },
                  },
                }}
              />
            </div>
          </div>

          <div
            className="p-4 rounded-xl text-xs leading-relaxed mt-4"
            style={{ background: 'rgba(61,122,82,0.08)', border: '1px solid rgba(61,122,82,0.25)', color: '#E8E8E4' }}
          >
            <p className="font-semibold text-[#3D7A52] mb-1">Nota fiscale</p>
            <p className="text-[#888780]">
              Tassazione agevolata al <strong>12,5%</strong> — più bassa rispetto al 26% di azioni ed ETF e al 26% dei conti deposito.
              Tassi BTP e conto deposito aggiornati a {parametri.data_aggiornamento}. Verificare le condizioni attuali prima di investire.
            </p>
          </div>
        </>
      )}
</motion.div>
  );
};

export default BuoniFruttiferiPostaliCalc;
